import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  Package,
  Building2,
  Plus,
  Trash2,
  Search,
  Check,
  ShoppingCart,
  Tag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const SupplierCatalogPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [catalogItems, setCatalogItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({
    articleNumberColumn: 0,
    nameColumn: 1,
    categoryColumn: null,
    priceNetColumn: null,
    priceGrossColumn: null,
    packageSizeColumn: null,
    packagePriceNetColumn: null,
    packagePriceGrossColumn: null,
    skipFirstRow: true,
    clearExisting: false
  });
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (selectedSupplier) {
      fetchCatalog();
    }
  }, [selectedSupplier]);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`);
      setSuppliers(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Lieferanten");
    }
  };

  const fetchCatalog = async () => {
    if (!selectedSupplier) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/supplier-catalog?supplier_id=${selectedSupplier}`);
      setCatalogItems(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden des Katalogs");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const res = await axios.post(`${API}/import/preview`, formData);
      setPreview(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Vorschau");
    }
  };

  const handleImport = async () => {
    if (!file || !selectedSupplier) {
      toast.error("Bitte Lieferant und Datei auswählen");
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('supplier_id', selectedSupplier);
    formData.append('article_number_column', mapping.articleNumberColumn);
    formData.append('name_column', mapping.nameColumn);
    if (mapping.categoryColumn !== null) formData.append('category_column', mapping.categoryColumn);
    if (mapping.priceNetColumn !== null) formData.append('price_net_column', mapping.priceNetColumn);
    if (mapping.priceGrossColumn !== null) formData.append('price_gross_column', mapping.priceGrossColumn);
    if (mapping.packageSizeColumn !== null) formData.append('package_size_column', mapping.packageSizeColumn);
    if (mapping.packagePriceNetColumn !== null) formData.append('package_price_net_column', mapping.packagePriceNetColumn);
    if (mapping.packagePriceGrossColumn !== null) formData.append('package_price_gross_column', mapping.packagePriceGrossColumn);
    formData.append('skip_first_row', mapping.skipFirstRow);
    formData.append('clear_existing', mapping.clearExisting);

    try {
      const res = await axios.post(`${API}/supplier-catalog/import`, formData);
      toast.success(`Import erfolgreich: ${res.data.added} neu, ${res.data.updated} aktualisiert`);
      setImportDialogOpen(false);
      setFile(null);
      setPreview(null);
      fetchCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  };

  const handleAddToInventory = async (item) => {
    try {
      const res = await axios.post(`${API}/supplier-catalog/${item.id}/add-to-inventory`);
      toast.success(res.data.message);
      fetchCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  const handleDeleteCatalog = async () => {
    if (!selectedSupplier) return;
    if (!window.confirm("Gesamten Katalog dieses Lieferanten löschen?")) return;
    
    try {
      await axios.delete(`${API}/supplier-catalog/supplier/${selectedSupplier}`);
      toast.success("Katalog gelöscht");
      fetchCatalog();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const filteredItems = catalogItems.filter(item => 
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.article_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedSupplierName = suppliers.find(s => s.id === selectedSupplier)?.name || "";

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800">
            Lieferanten-Katalog
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Komplettes Sortiment eines Lieferanten importieren und verwalten
          </p>
        </div>
      </motion.div>

      {/* Supplier Selection & Actions */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Lieferant auswählen</Label>
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger data-testid="supplier-select">
                  <SelectValue placeholder="Lieferant wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedSupplier && (
              <>
                <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-rose-600 hover:bg-rose-700">
                      <Upload className="w-4 h-4 mr-2" />
                      Katalog importieren
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Katalog importieren - {selectedSupplierName}</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      {/* File Upload */}
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          accept=".csv,.txt"
                          onChange={handleFileChange}
                          className="hidden"
                          id="catalog-file-upload"
                        />
                        <label htmlFor="catalog-file-upload" className="cursor-pointer">
                          <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                          <p className="text-slate-600">
                            {file ? file.name : "CSV-Datei auswählen"}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Semikolon-getrennte CSV-Datei
                          </p>
                        </label>
                      </div>

                      {/* Preview & Mapping */}
                      {preview && (
                        <>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <Label>Artikelnummer *</Label>
                              <Select 
                                value={mapping.articleNumberColumn?.toString()} 
                                onValueChange={(v) => setMapping({...mapping, articleNumberColumn: parseInt(v)})}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {preview.headers.map((h, i) => (
                                    <SelectItem key={i} value={i.toString()}>{h || `Spalte ${i+1}`}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Produktname *</Label>
                              <Select 
                                value={mapping.nameColumn?.toString()} 
                                onValueChange={(v) => setMapping({...mapping, nameColumn: parseInt(v)})}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {preview.headers.map((h, i) => (
                                    <SelectItem key={i} value={i.toString()}>{h || `Spalte ${i+1}`}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Kategorie</Label>
                              <Select 
                                value={mapping.categoryColumn?.toString() || "none"} 
                                onValueChange={(v) => setMapping({...mapping, categoryColumn: v === "none" ? null : parseInt(v)})}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Optional" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">- Keine -</SelectItem>
                                  {preview.headers.map((h, i) => (
                                    <SelectItem key={i} value={i.toString()}>{h || `Spalte ${i+1}`}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Preis Netto</Label>
                              <Select 
                                value={mapping.priceNetColumn?.toString() || "none"} 
                                onValueChange={(v) => setMapping({...mapping, priceNetColumn: v === "none" ? null : parseInt(v)})}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Optional" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">- Keine -</SelectItem>
                                  {preview.headers.map((h, i) => (
                                    <SelectItem key={i} value={i.toString()}>{h || `Spalte ${i+1}`}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Preis Brutto</Label>
                              <Select 
                                value={mapping.priceGrossColumn?.toString() || "none"} 
                                onValueChange={(v) => setMapping({...mapping, priceGrossColumn: v === "none" ? null : parseInt(v)})}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Optional" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">- Keine -</SelectItem>
                                  {preview.headers.map((h, i) => (
                                    <SelectItem key={i} value={i.toString()}>{h || `Spalte ${i+1}`}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>VPE (Stück)</Label>
                              <Select 
                                value={mapping.packageSizeColumn?.toString() || "none"} 
                                onValueChange={(v) => setMapping({...mapping, packageSizeColumn: v === "none" ? null : parseInt(v)})}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Optional" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">- Keine -</SelectItem>
                                  {preview.headers.map((h, i) => (
                                    <SelectItem key={i} value={i.toString()}>{h || `Spalte ${i+1}`}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <Checkbox 
                                id="skip-first" 
                                checked={mapping.skipFirstRow}
                                onCheckedChange={(c) => setMapping({...mapping, skipFirstRow: c})}
                              />
                              <Label htmlFor="skip-first">Erste Zeile überspringen (Kopfzeile)</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox 
                                id="clear-existing" 
                                checked={mapping.clearExisting}
                                onCheckedChange={(c) => setMapping({...mapping, clearExisting: c})}
                              />
                              <Label htmlFor="clear-existing" className="text-amber-600">
                                Bestehenden Katalog vorher löschen
                              </Label>
                            </div>
                          </div>

                          {/* Preview Table */}
                          <div className="border rounded-lg overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  {preview.headers.map((h, i) => (
                                    <TableHead key={i} className="text-xs whitespace-nowrap">
                                      {h || `Spalte ${i+1}`}
                                    </TableHead>
                                  ))}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {preview.preview_rows.map((row, i) => (
                                  <TableRow key={i}>
                                    {row.map((cell, j) => (
                                      <TableCell key={j} className="text-xs">{cell}</TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          
                          <p className="text-sm text-slate-500">
                            Vorschau zeigt die ersten 5 Zeilen von {preview.total_rows} Einträgen.
                            MwSt wird automatisch erkannt (Snacks: 7%, Getränke: 19%).
                          </p>
                        </>
                      )}
                    </div>

                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline">Abbrechen</Button>
                      </DialogClose>
                      <Button 
                        onClick={handleImport} 
                        disabled={!file || !preview || importing}
                        className="bg-rose-600 hover:bg-rose-700"
                      >
                        {importing ? "Importiere..." : "Katalog importieren"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {catalogItems.length > 0 && (
                  <Button variant="outline" onClick={handleDeleteCatalog}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Katalog löschen
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Catalog Items */}
      {selectedSupplier && (
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <Package className="w-5 h-5" />
                Katalog: {selectedSupplierName}
                <Badge variant="secondary">{catalogItems.length} Produkte</Badge>
              </CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Lade Katalog...</div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">
                  {catalogItems.length === 0 
                    ? "Noch kein Katalog importiert. Klicken Sie auf 'Katalog importieren'."
                    : "Keine Produkte gefunden."}
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Art.-Nr.</TableHead>
                      <TableHead>Produktname</TableHead>
                      <TableHead>Kategorie</TableHead>
                      <TableHead className="text-right">Preis (Netto)</TableHead>
                      <TableHead className="text-right">VPE</TableHead>
                      <TableHead className="text-right">MwSt</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.slice(0, 100).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-sm">{item.article_number}</TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell>
                          {item.category && (
                            <Badge variant="outline">{item.category}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.price_net > 0 ? `${item.price_net.toFixed(2)} €` : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.package_size > 1 ? item.package_size : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={item.vat_rate === 7 ? "secondary" : "outline"}>
                            {item.vat_rate}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.in_own_inventory ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <Check className="w-3 h-3 mr-1" />
                              Im Sortiment
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">
                              Nur Katalog
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {!item.in_own_inventory && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAddToInventory(item)}
                              className="text-rose-600 hover:text-rose-700"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Sortiment
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredItems.length > 100 && (
                  <div className="p-3 text-center text-sm text-slate-500 border-t">
                    Zeige 100 von {filteredItems.length} Produkten. Nutzen Sie die Suche, um weitere zu finden.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupplierCatalogPage;
